import { useCallback, useEffect, useRef, useState } from "react"
import type { SettingsSection } from "./types"

const SECTIONS: readonly SettingsSection[] = ["companion", "connections", "models", "schedules"]
const DEFAULT_SECTION: SettingsSection = "companion"
const HASH_RE = /^#settings\/([a-z]+)$/

function parseHash(hash: string): SettingsSection | null {
  const match = HASH_RE.exec(hash)
  if (!match) return null
  const candidate = match[1]
  return (SECTIONS as readonly string[]).includes(candidate) ? (candidate as SettingsSection) : null
}

// Module-scoped "last visited section" — deliberately NOT React state. It
// lets AppShell's "open settings" trigger reopen the surface where the user
// left it without lifting state up into AppShell or running a second
// hashchange listener there. SettingsSurface mounts exactly once at
// App-lifetime, so there is exactly one writer to this module variable.
let lastSection: SettingsSection = DEFAULT_SECTION

/** Test-only: reset the module-scoped "last visited section" memory. Kairo
 *  flagged that `lastSection` has no reset mechanism, making any test of it
 *  order-dependent — this is that mechanism, never called in production
 *  (SettingsSurface mounts once at App-lifetime; there is exactly one real
 *  writer for the app's entire life). Mirrors the gateway's underscore-
 *  prefixed test-seam export convention (server.ts's `_turnBuffersBySession`
 *  etc.) rather than vi.resetModules(), which would risk duplicating the
 *  "react" module instance underneath @testing-library/react's static import. */
export function _resetLastSectionForTests(): void {
  lastSection = DEFAULT_SECTION
  pendingScheduleOrigin = null
}

/** Module-scoped one-shot prefill for "Wake me on this" (`SessionItem`'s
 *  context menu). Mirrors `lastSection` above — the same module-scoped,
 *  single-writer shape — rather than the URL hash, so a chat's session id
 *  never lands in browser history. Read-and-clear: a later, unrelated visit
 *  to Settings must never silently inherit a stale binding. */
let pendingScheduleOrigin: { sessionId: string; label: string | null } | null = null

export function setPendingScheduleOrigin(value: { sessionId: string; label: string | null }): void {
  pendingScheduleOrigin = value
}

/** Reads and clears the pending origin in one step — one-shot by construction. */
export function takePendingScheduleOrigin(): { sessionId: string; label: string | null } | null {
  const value = pendingScheduleOrigin
  pendingScheduleOrigin = null
  return value
}

/** Hash AppShell's "open settings" trigger navigates to — reopens on the
 *  last section this hook's owner visited, or 'companion' on a cold start. */
export function settingsHash(section?: SettingsSection): string {
  return `#settings/${section ?? lastSection}`
}

export interface UseSettingsSurface {
  isOpen: boolean
  section: SettingsSection
  /** Switch section while the surface is open. REPLACES the history entry
   *  (never pushes) so Back leaves settings instead of walking sections
   *  backwards — see the ordering contract in the module doc above. */
  show: (section: SettingsSection) => void
  /** Closes the surface. Pops the history entry this surface pushed to open
   *  (mirrors the Back button) when one exists; falls back to clearing the
   *  hash in place for a cold-loaded deep link with nothing to pop. */
  close: () => void
}

export function useSettingsSurface(): UseSettingsSurface {
  const [state, setState] = useState<{ open: boolean; section: SettingsSection }>(() => {
    const parsed = parseHash(window.location.hash)
    if (parsed) lastSection = parsed
    return { open: parsed !== null, section: parsed ?? lastSection }
  })

  // True exactly when the current settings hash is one this hook instance
  // (or the plain `location.hash =` assignment AppShell's opener uses — both
  // create a fresh history entry) pushed during this page's lifetime, i.e.
  // safe to pop with history.back() on close. False for a hash that was
  // already present at mount (a reload or a deep link straight into
  // '#settings/...') — there is no guarantee anything sane exists to pop
  // back to, so close() clears the hash in place instead.
  const canPopRef = useRef(false)

  useEffect(() => {
    function onHashChange() {
      const parsed = parseHash(window.location.hash)
      setState((prev) => {
        if (parsed) {
          lastSection = parsed
          if (!prev.open) canPopRef.current = true
          return { open: true, section: parsed }
        }
        canPopRef.current = false
        return prev.open ? { open: false, section: prev.section } : prev
      })
    }
    window.addEventListener("hashchange", onHashChange)
    return () => window.removeEventListener("hashchange", onHashChange)
  }, [])

  const show = useCallback((section: SettingsSection) => {
    lastSection = section
    if (parseHash(window.location.hash) !== null) {
      // Already inside settings — REPLACE so switching sections never grows
      // the history stack (Back must leave settings, not walk backwards
      // through the sections that were visited on the way).
      window.history.replaceState(null, "", `#settings/${section}`)
    } else {
      window.history.pushState(null, "", `#settings/${section}`)
      canPopRef.current = true
    }
    // pushState/replaceState fire neither `hashchange` nor `popstate` — update
    // local state directly rather than relying on the listener above.
    setState({ open: true, section })
  }, [])

  const close = useCallback(() => {
    if (canPopRef.current) {
      canPopRef.current = false
      window.history.back()
      // The resulting popstate/hashchange (the hash actually changes) drives
      // isOpen to false via the listener above.
      return
    }
    const { pathname, search } = window.location
    window.history.replaceState(null, "", pathname + search)
    setState((prev) => ({ ...prev, open: false }))
  }, [])

  return { isOpen: state.open, section: state.section, show, close }
}
